import React, { useState, useEffect, useRef, useMemo } from "react";

import ConfidenceIndicator from "./ConfidenceIndicator.jsx";

// FieldEditor — inline editable field with confidence indicator and
// validation issue display.
//
// Props:
//   fieldPath         Dotted path (e.g. "line_items.2.gross_budget")
//   value             Current value
//   confidence        0-1 number or null
//   validationIssues  Array of { code, message, severity, suggestion? } for
//                     this field path, or undefined
//   type              'text' | 'number' | 'date' | 'select' (inferred from
//                     value if omitted)
//   options           [{ value, label }] for selects
//   label             Display label
//   onChange(newValue) Called on blur (not every keystroke) to avoid API spam
//
// The component keeps local state while editing and only fires onChange on
// blur, so the parent IO record stays stable during typing.

function inferType(value) {
  if (typeof value === "number") return "number";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return "date";
  return "text";
}

export default function FieldEditor({
  fieldPath,
  value,
  confidence,
  validationIssues,
  type,
  options,
  label,
  onChange,
}) {
  const fieldType = type || inferType(value);
  const [localValue, setLocalValue] = useState(value == null ? "" : value);
  const [focused, setFocused] = useState(false);
  const lastCommitted = useRef(value);

  // Keep local value synced when the parent's value changes (e.g. after save).
  useEffect(() => {
    if (!focused) {
      setLocalValue(value == null ? "" : value);
      lastCommitted.current = value;
    }
  }, [value, focused]);

  const issues = validationIssues || [];
  const hasError = issues.some((i) => i.severity === "error");
  const hasWarning = issues.some((i) => i.severity === "warning");

  const fieldClasses = useMemo(() => {
    const classes = ["field-editor-control"];
    if (hasError) classes.push("has-error");
    else if (hasWarning) classes.push("has-warning");
    return classes.join(" ");
  }, [hasError, hasWarning]);

  function commit() {
    setFocused(false);
    let next = localValue;
    // Coerce types so the parent record stays typed correctly.
    if (fieldType === "number") {
      if (next === "" || next == null) next = null;
      else next = Number(next);
    } else if (fieldType === "select") {
      // keep as string
    }
    if (next !== lastCommitted.current) {
      lastCommitted.current = next;
      if (onChange) onChange(next);
    }
  }

  function onBlur() {
    commit();
  }

  function onKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      setLocalValue(lastCommitted.current == null ? "" : lastCommitted.current);
      e.currentTarget.blur();
    }
  }

  let control;
  if (fieldType === "select") {
    control = (
      <select
        className={fieldClasses}
        value={localValue ?? ""}
        disabled={!onChange}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={commit}
        data-field={fieldPath}
      >
        <option value="">—</option>
        {(options || []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (fieldType === "number") {
    control = (
      <input
        type="number"
        className={fieldClasses}
        value={localValue ?? ""}
        placeholder={label}
        disabled={!onChange}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        data-field={fieldPath}
        step="any"
      />
    );
  } else if (fieldType === "date") {
    control = (
      <input
        type="date"
        className={fieldClasses}
        value={localValue ?? ""}
        disabled={!onChange}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        data-field={fieldPath}
      />
    );
  } else {
    control = (
      <input
        type="text"
        className={fieldClasses}
        value={localValue ?? ""}
        placeholder={label}
        disabled={!onChange}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        data-field={fieldPath}
      />
    );
  }

  return (
    <div className={`field-editor ${hasError ? "field-error" : ""} ${hasWarning ? "field-warning" : ""}`}>
      <div className="field-editor-row">
        {control}
        <ConfidenceIndicator confidence={confidence} />
      </div>
      {label && <label className="field-editor-label">{label}</label>}
      {issues.length > 0 && (
        <ul className="field-editor-issues">
          {issues.map((issue, idx) => (
            <li key={`${issue.code}-${idx}`} className={`issue-${issue.severity}`}>
              <span className="issue-code">{issue.code}</span>
              <span className="issue-message">{issue.message}</span>
              {issue.suggestion && <span className="issue-suggestion">↳ {issue.suggestion}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}